import React, { useEffect, useState } from 'react';
import { C, ACCENT } from '../../theme';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { ChevronRight, ChevronDown } from 'lucide-react';

function fmt(n) { return n >= 1e6 ? (n/1e6).toFixed(2)+'M' : n >= 1e3 ? (n/1e3).toFixed(0)+'K' : Math.round(n).toLocaleString(); }
function eur(n) { return '€'+(n>=1e6?(n/1e6).toFixed(2)+'M':n>=1e3?(n/1e3).toFixed(0)+'K':Math.round(n).toLocaleString()); }

const TYPE_LABELS = { banner:'Banner', branding:'Branding', nonstandard:'Non-standard', sem:'SEM', social:'Social', video:'Video', other:'Other' };
function typeLabel(t) { return TYPE_LABELS[t] || (t ? t.charAt(0).toUpperCase()+t.slice(1) : 'Other'); }

function shareOf(part, whole) { return whole > 0 ? (part / whole * 100).toFixed(1) + '%' : '—'; }

// Merges `overrides` onto `base`, skipping any key whose value is undefined — so a depth's style
// (e.g. DEPTH_STYLE[0].color === undefined, meaning "inherit C.td's own color") doesn't silently blank
// out a property C.td already defines. Plain object spread would instead keep the literal `undefined`
// and erase C.td's value entirely, which is exactly the bug this replaces (depth-0 label text was
// rendering with no color at all instead of falling back to C.td's #1a2a3a).
function mergeStyle(base, overrides) {
  const out = { ...base };
  for (const k in overrides) if (overrides[k] !== undefined) out[k] = overrides[k];
  return out;
}

// Shared column widths so the outer "By Client" table and the nested PlatformTree table form one
// continuous, pixel-aligned set of Budget/Impressions/Share columns instead of two independently
// auto-sized tables. Both tables use table-layout:fixed + an explicit <colgroup> built from these.
// LABEL_WIDTH is the first column's width in a table that starts flush with the page's left edge
// (the standalone "By Platform" table). When PlatformTree is nested inside "By Client"'s
// expanded row, its containing <td> adds NEST_INDENT of left padding to visually indent it under the
// client row — so that instance's label column is shrunk by NEST_INDENT to compensate, keeping every
// column after it (Budget/Impressions/Share) starting at the exact same absolute x as the outer table.
const COL_LABEL = 460;
const COL_BUDGET = 110;
const COL_IMPR = 130;
const COL_SHARE = 90;
// Nested PlatformTree sits inside a padded <td>, so its own table also draws C.table's 1px left
// border before its first column starts — 41 (not the 40px visual indent alone) compensates for that
// border pixel too, confirmed by measuring rendered getBoundingClientRect() until it matched exactly.
const NEST_INDENT = 41;
const NEST_PADDING = 40; // the actual visual left-padding on the wrapping <td> (indentation only)

// Flattens a Type → Seller → Format tree (as returned by the backend) into a flat leaf list —
// {type, seller, name, site_id, budget, impressions} per format. Used both by the site-grouped
// "By Website & Format" view (groups by site, ignoring type/seller) and by buildGroupedTree below
// (regroups by whichever grouping preset is selected).
function flattenTree(tree) {
  const out = [];
  (tree || []).forEach(t => {
    (t.sellers || []).forEach(s => {
      (s.formats || []).forEach(f => {
        out.push({ type: t.type, seller: s.seller, name: f.name, site_id: f.site_id, budget: f.budget, impressions: f.impressions });
      });
    });
  });
  return out;
}

// Grouping presets for the "Group by" control. `keys` is an ordered list (1-3) of leaf fields to
// nest by, consumed by buildGroupedTree. Label doubles as the tree's first-column header.
const GROUP_DEF = {
  type:   { field: 'type',   label: v => typeLabel(v) },
  seller: { field: 'seller', label: v => v },
  format: { field: 'name',   label: v => v },
};
const GROUPING_PRESETS = [
  { id: 'type-seller-format', label: 'Type → Seller → Format', keys: ['type', 'seller', 'format'] },
  { id: 'seller-type-format', label: 'Seller → Type → Format', keys: ['seller', 'type', 'format'] },
  { id: 'type',               label: 'Type only',              keys: ['type'] },
  { id: 'seller',             label: 'Seller only',            keys: ['seller'] },
  { id: 'format',             label: 'Format only',            keys: ['format'] },
  { id: 'seller-format',      label: 'Seller → Format',        keys: ['seller', 'format'] },
];

// Regroups a flat leaf list into a nested tree per an ordered list of grouping keys (1-3 of
// type/seller/format). Depth of the resulting tree == keys.length; a node's `children` is null once
// there are no more keys to group by, which PlatformTree treats as a leaf (no expand chevron).
// Budget/impressions are summed at every level, so Share (computed against the immediate parent by
// PlatformTree) rolls up correctly regardless of which preset produced the tree.
function buildGroupedTree(flat, keys) {
  function group(items, idx) {
    if (idx >= keys.length) return null;
    const def = GROUP_DEF[keys[idx]];
    const buckets = new Map();
    items.forEach(it => {
      const raw = it[def.field];
      if (!buckets.has(raw)) buckets.set(raw, []);
      buckets.get(raw).push(it);
    });
    const nodes = [...buckets.entries()].map(([raw, bucketItems]) => ({
      key: String(raw),
      label: def.label(raw),
      groupKind: keys[idx],
      siteId: keys[idx] === 'format' ? bucketItems[0].site_id : null,
      budget: bucketItems.reduce((s, x) => s + x.budget, 0),
      impressions: bucketItems.reduce((s, x) => s + x.impressions, 0),
      children: group(bucketItems, idx + 1),
    }));
    nodes.sort((a, b) => b.budget - a.budget || a.label.localeCompare(b.label));
    return nodes;
  }
  return group(flat, 0) || [];
}

// Per-depth styling (font size / color / weight / indent), matching the original hand-built
// Type → Seller → Format look: depth 0 bold and full-size, depth 1 medium, depth 2 (or deeper) muted.
const DEPTH_STYLE = [
  { paddingLeft: undefined, fontSize: undefined, fontWeight: 700, color: undefined, chevron: 14, chevronColor: ACCENT, chevronGap: 8 },
  { paddingLeft: 36, fontSize: 13, fontWeight: 600, color: '#2a4a6a', chevron: 12, chevronColor: '#6a8aaa', chevronGap: 7 },
  { paddingLeft: 62, fontSize: 12, fontWeight: undefined, color: '#4a6a8a', chevron: 12, chevronColor: '#6a8aaa', chevronGap: 7 },
];

// Shared accordion, 1-3 levels deep depending on the tree buildGroupedTree produced. Collapsed by
// default at every level. A node with no children (either a true leaf, or every node when a
// single-key preset is selected) renders with no chevron and isn't clickable — degrades cleanly to a
// flat table for "Type only" / "Seller only" / "Format only".
// `parentBudget` is the Share denominator for the top (depth 0) level; deeper levels use their own
// parent node's budget. `indent` is the left padding already applied by the caller's wrapper (0 for
// the standalone "By Platform" table, NEST_INDENT when nested inside "By Client"'s expanded
// row) — subtracted from the label column's width so Budget/Impressions/Share still start at the same
// absolute x position as the outer "By Client" table's matching columns.
function PlatformTree({ tree, hasSites, siteNames, parentBudget, indent = 0, columnLabel = 'Type / Seller / Format', showTotal = false }) {
  const [openPaths, setOpenPaths] = useState({});

  if (!tree || tree.length === 0) {
    return <div style={C.empty}>No data.</div>;
  }

  const labelWidth = COL_LABEL - indent;
  // Summed straight from the top-level nodes of whichever tree buildGroupedTree produced — correct
  // for any preset, since the top-level nodes already sum to the grand total regardless of grouping.
  const treeBudget = tree.reduce((s, n) => s + n.budget, 0);
  const treeImpressions = tree.reduce((s, n) => s + n.impressions, 0);

  function renderNodes(nodes, depth, parentPath, parentTotal) {
    const st = DEPTH_STYLE[Math.min(depth, DEPTH_STYLE.length - 1)];
    return nodes.map(node => {
      const path = parentPath ? `${parentPath}::${node.key}` : node.key;
      const hasChildren = !!(node.children && node.children.length);
      const isOpen = !!openPaths[path];
      const rowStyle = depth === 0
        ? { ...C.tr, background: isOpen ? 'rgba(0,144,208,0.04)' : 'transparent' }
        : { cursor: hasChildren ? 'pointer' : 'default', borderBottom: '1px solid #eef4f8', background: hasChildren ? (isOpen ? 'rgba(0,144,208,0.05)' : 'rgba(0,144,208,0.02)') : 'rgba(0,144,208,0.04)' };

      return (
        <React.Fragment key={path}>
          <tr
            style={rowStyle}
            onClick={hasChildren ? () => setOpenPaths(p => ({ ...p, [path]: !p[path] })) : undefined}
            onMouseEnter={hasChildren ? e => { if (!isOpen) e.currentTarget.style.background = depth === 0 ? 'rgba(0,144,208,0.03)' : 'rgba(0,144,208,0.05)'; } : undefined}
            onMouseLeave={hasChildren ? e => { if (!isOpen) e.currentTarget.style.background = depth === 0 ? 'transparent' : 'rgba(0,144,208,0.02)'; } : undefined}>
            <td style={mergeStyle(C.td, { paddingLeft: st.paddingLeft, fontSize: st.fontSize, fontWeight: st.fontWeight, color: st.color })}>
              {hasChildren && (isOpen
                ? <ChevronDown size={st.chevron} style={{ verticalAlign: 'middle', marginRight: st.chevronGap, color: st.chevronColor }}/>
                : <ChevronRight size={st.chevron} style={{ verticalAlign: 'middle', marginRight: st.chevronGap, color: st.chevronColor }}/>)}
              {node.groupKind === 'format' && hasSites && node.siteId && siteNames[node.siteId] && (
                <span style={{ color: '#8aa0b8', marginRight: 6, fontSize: 11 }}>{siteNames[node.siteId]} /</span>
              )}
              {node.label}
            </td>
            <td style={mergeStyle(C.td, { textAlign: 'right', fontSize: st.fontSize, fontWeight: depth === 0 ? 700 : undefined })}>{eur(node.budget)}</td>
            <td style={mergeStyle(C.td, { textAlign: 'right', fontSize: st.fontSize, color: '#6a8aaa' })}>{fmt(node.impressions)}</td>
            <td style={mergeStyle(C.td, { textAlign: 'right', fontSize: st.fontSize, color: depth === 0 ? '#6a8aaa' : '#8aa0b8' })}>{shareOf(node.budget, parentTotal)}</td>
          </tr>
          {hasChildren && isOpen && renderNodes(node.children, depth + 1, path, node.budget)}
        </React.Fragment>
      );
    });
  }

  return (
    <table style={{ ...C.table, marginBottom: 0, width: labelWidth + COL_BUDGET + COL_IMPR + COL_SHARE, tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: labelWidth }}/>
        <col style={{ width: COL_BUDGET }}/>
        <col style={{ width: COL_IMPR }}/>
        <col style={{ width: COL_SHARE }}/>
      </colgroup>
      <thead>
        <tr>
          <th style={C.th}>{columnLabel}</th>
          <th style={{ ...C.th, textAlign: 'right' }}>Budget</th>
          <th style={{ ...C.th, textAlign: 'right' }}>Impressions</th>
          <th style={{ ...C.th, textAlign: 'right' }}>Share</th>
        </tr>
      </thead>
      <tbody>
        {renderNodes(tree, 0, '', parentBudget)}
      </tbody>
      {showTotal && (
        <tfoot>
          <tr style={{ borderTop: '2px solid #dce8f0' }}>
            <td style={{ ...C.td, fontWeight: 700 }}>Total</td>
            <td style={{ ...C.td, textAlign: 'right', fontWeight: 700 }}>{eur(treeBudget)}</td>
            <td style={{ ...C.td, textAlign: 'right', fontWeight: 700 }}>{fmt(treeImpressions)}</td>
            <td style={C.td}></td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

export default function AgencySummaryPage() {
  const { hasSites } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [groupingId, setGroupingId] = useState(GROUPING_PRESETS[0].id);
  const grouping = GROUPING_PRESETS.find(g => g.id === groupingId) || GROUPING_PRESETS[0];

  useEffect(() => {
    api.getAgencySummary()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{...C.page, color:'#8aa0b8'}}>Loading…</div>;
  if (!data) return <div style={{...C.page, color:'#d04040'}}>Failed to load summary.</div>;

  const { clients, platforms, site_names } = data;
  const totalBudget = clients.reduce((s, c) => s + c.total_budget, 0);
  const totalImpressions = clients.reduce((s, c) => s + c.total_impressions, 0);

  // Flat leaf list, independent of the selected grouping preset — used for the "Platforms" KPI
  // (a stable count of distinct formats) and for the site-grouped "By Website & Format" view.
  const flatPlatforms = flattenTree(platforms);

  const bysite = {};
  if (hasSites) {
    flatPlatforms.forEach(p => {
      const sname = site_names[p.site_id] || 'Other';
      if (!bysite[sname]) bysite[sname] = [];
      bysite[sname].push(p);
    });
  }

  // Re-groups the org-wide platforms tree per the selected preset, for the standalone
  // "By Platform" table (non-hasSites orgs only).
  const formatTree = hasSites ? null : buildGroupedTree(flatPlatforms, grouping.keys);

  function toggleClient(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div style={C.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={C.title}>Agency Summary</h1>
        <p style={{ color: '#8aa0b8', fontSize: 13, marginTop: 4 }}>Aggregated across all clients and plans</p>
      </div>

      {/* KPI bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Budget', value: eur(totalBudget) },
          { label: 'Total Impressions', value: fmt(totalImpressions) },
          { label: 'Clients', value: clients.length },
          { label: 'Platforms', value: flatPlatforms.length },
        ].map(k => (
          <div key={k.label} style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.8)', border: '1px solid #dce8f0', borderRadius: 12, minWidth: 120 }}>
            <div style={{ fontSize: 11, color: '#8aa0b8', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1a2a3a' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Grouping control — shared by "By Client" (nested rows) and "By Platform" below,
          since both are just different regroupings of the same underlying leaf-level data. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#6a8aaa' }} htmlFor="agency-summary-grouping">Group by</label>
        <select
          id="agency-summary-grouping"
          value={groupingId}
          onChange={e => setGroupingId(e.target.value)}
          style={{ ...C.input, width: 'auto', padding: '7px 12px' }}>
          {GROUPING_PRESETS.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
        </select>
      </div>

      {/* By Client */}
      <div style={{ marginBottom: 36 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a2a3a', marginBottom: 12 }}>By Client</h2>
        <table style={{ ...C.table, width: COL_LABEL + COL_BUDGET + COL_IMPR + COL_SHARE, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: COL_LABEL }}/>
            <col style={{ width: COL_BUDGET }}/>
            <col style={{ width: COL_IMPR }}/>
            <col style={{ width: COL_SHARE }}/>
          </colgroup>
          <thead>
            <tr>
              <th style={C.th}>Client</th>
              <th style={{ ...C.th, textAlign: 'right' }}>Budget</th>
              <th style={{ ...C.th, textAlign: 'right' }}>Impressions</th>
              <th style={{ ...C.th, textAlign: 'right' }}>Share</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(cl => (
              <React.Fragment key={cl.id}>
                <tr style={{ ...C.tr, cursor: 'pointer', background: expanded[cl.id] ? 'rgba(0,144,208,0.04)' : 'transparent' }}
                  onClick={() => toggleClient(cl.id)}
                  onMouseEnter={e => { if (!expanded[cl.id]) e.currentTarget.style.background = 'rgba(0,144,208,0.03)'; }}
                  onMouseLeave={e => { if (!expanded[cl.id]) e.currentTarget.style.background = 'transparent'; }}>
                  <td style={{ ...C.td, fontWeight: 700 }}>
                    <span style={{ marginRight: 8, color: ACCENT, fontSize: 10 }}>{expanded[cl.id] ? '▼' : '▶'}</span>
                    {cl.name}
                  </td>
                  <td style={{ ...C.td, textAlign: 'right', fontWeight: 600 }}>{eur(cl.total_budget)}</td>
                  <td style={{ ...C.td, textAlign: 'right', color: '#6a8aaa' }}>{fmt(cl.total_impressions)}</td>
                  <td style={{ ...C.td, textAlign: 'right', color: '#6a8aaa' }}>
                    {totalBudget > 0 ? (cl.total_budget / totalBudget * 100).toFixed(1) + '%' : '—'}
                  </td>
                </tr>
                {expanded[cl.id] && (
                  <tr>
                    <td colSpan={4} style={{ padding: `0 20px 16px ${NEST_PADDING}px`, border: 'none' }}>
                      <PlatformTree
                        tree={buildGroupedTree(flattenTree(cl.platforms), grouping.keys)}
                        hasSites={hasSites} siteNames={site_names}
                        parentBudget={cl.total_budget} indent={NEST_INDENT} columnLabel={grouping.label}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #dce8f0' }}>
              <td style={{ ...C.td, fontWeight: 700 }}>Total</td>
              <td style={{ ...C.td, textAlign: 'right', fontWeight: 700 }}>{eur(totalBudget)}</td>
              <td style={{ ...C.td, textAlign: 'right', fontWeight: 700 }}>{fmt(totalImpressions)}</td>
              <td style={C.td}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* By Format — grouped by site for publisher orgs, dynamic-grouping accordion for agencies */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#1a2a3a', marginBottom: 12 }}>
          {hasSites ? 'By Website & Format' : 'By Platform'}
        </h2>

        {hasSites ? (
          Object.entries(bysite).sort((a, b) => {
            const sa = a[1].reduce((s,p)=>s+p.budget,0);
            const sb = b[1].reduce((s,p)=>s+p.budget,0);
            return sb - sa;
          }).map(([siteName, plats]) => {
            const siteBudget = plats.reduce((s, p) => s + p.budget, 0);
            const siteImpr = plats.reduce((s, p) => s + p.impressions, 0);
            return (
              <div key={siteName} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', background: 'rgba(0,144,208,0.06)', borderRadius: 8, marginBottom: 6, border: '1px solid rgba(0,144,208,0.15)' }}>
                  <span style={{ fontWeight: 700, color: '#1a2a3a', fontSize: 13 }}>{siteName}</span>
                  <span style={{ color: ACCENT, fontWeight: 700, fontSize: 13 }}>{eur(siteBudget)}</span>
                  <span style={{ color: '#8aa0b8', fontSize: 12 }}>{fmt(siteImpr)} impr</span>
                  <span style={{ color: '#8aa0b8', fontSize: 12, marginLeft: 'auto' }}>
                    {totalBudget > 0 ? (siteBudget / totalBudget * 100).toFixed(1) + '% of total' : ''}
                  </span>
                </div>
                <table style={{ ...C.table, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th style={C.th}>Format</th>
                      <th style={{ ...C.th, textAlign: 'right' }}>Budget</th>
                      <th style={{ ...C.th, textAlign: 'right' }}>Impressions</th>
                      <th style={{ ...C.th, textAlign: 'right' }}>Share of Site</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plats.map(p => (
                      <tr key={p.name} style={C.tr}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,144,208,0.03)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={C.td}>{p.name}</td>
                        <td style={{ ...C.td, textAlign: 'right' }}>{eur(p.budget)}</td>
                        <td style={{ ...C.td, textAlign: 'right', color: '#6a8aaa' }}>{fmt(p.impressions)}</td>
                        <td style={{ ...C.td, textAlign: 'right', color: '#8aa0b8' }}>
                          {siteBudget > 0 ? (p.budget / siteBudget * 100).toFixed(1) + '%' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })
        ) : (
          <PlatformTree tree={formatTree} hasSites={hasSites} siteNames={site_names} parentBudget={totalBudget} indent={0} columnLabel={grouping.label} showTotal />
        )}
      </div>
    </div>
  );
}
